import { Link, useParams, useLocation } from "react-router-dom";
import { useEffect } from "react";
import { ShoppingCart } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { formatBrlCurrency } from "@/lib/price";
import { buildProductMessage } from "@/lib/whatsapp";
import { trackProductViewOnce, trackWhatsAppClick } from "@/graphql/analytics";
import { client } from "@/lib/apolloClient";
import { useCart } from "@/contexts/CartContext";
import ProductImageGallery from "./components/ProductImageGallery";
import PaymentMethodSelector from "./components/PaymentMethodSelector";
import WhatsAppButton from "./components/WhatsAppButton";
import { useSelectedPaymentMethod } from "@/hooks/useSelectedPaymentMethod";
import { paymentMethodIdsFromGraphql } from "@/lib/paymentMethods/graphql";
import { usePublicProduct } from "./hooks/usePublicProduct";
import { usePublicStore } from "./hooks/usePublicStore";

export default function ProductDetailPage() {
  const { storeSlug, productSlug } = useParams<{
    storeSlug: string;
    productSlug: string;
  }>();

  const { product, loading, notFound } = usePublicProduct(storeSlug, productSlug);
  const { store: publicStore } = usePublicStore(storeSlug);
  const { addItem, hasItem, canAddMore, isReady, getItemQuantity } = useCart();
  const location = useLocation();
  const acceptedPaymentMethods = paymentMethodIdsFromGraphql(
    publicStore?.acceptedPaymentMethods
  );
  const [paymentMethodId, setPaymentMethodId] =
    useSelectedPaymentMethod(acceptedPaymentMethods);

  useEffect(() => {
    if (!product || !publicStore) return;
    trackProductViewOnce(client, publicStore.id, product.id);
  }, [product?.id, publicStore?.id]);

  if (loading) {
    return (
      <div className="container-elegant page-padding">
        <Skeleton className="h-5 w-40 mb-8" />
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-2">
          <Skeleton className="aspect-square w-full" />
          <div className="space-y-4">
            <Skeleton className="h-10 w-3/4" />
            <Skeleton className="h-5 w-24" />
            <Skeleton className="h-8 w-32" />
            <Skeleton className="h-24 w-full" />
          </div>
        </div>
      </div>
    );
  }

  if (notFound || !product) {
    return (
      <div className="container-elegant page-padding flex flex-col items-center justify-center min-h-[50vh] gap-4">
        <p className="text-lg font-light text-primary-600 dark:text-primary-400">
          Produto não encontrado
        </p>
        {storeSlug && (
          <Link
            to={`/stores/${storeSlug}`}
            className="text-sm font-light text-primary-700 dark:text-primary-300 underline underline-offset-4 hover:text-primary-900 dark:hover:text-white"
          >
            ← Voltar para a loja
          </Link>
        )}
      </div>
    );
  }

  const hasComparePrice =
    product.compareAtPriceCents != null &&
    product.compareAtPriceCents > product.priceCents;

  return (
    <div className="container-elegant page-padding">
      <Link
        to={`/stores/${storeSlug}`}
        className="inline-block mb-8 text-sm font-light text-primary-700 dark:text-primary-300 hover:text-primary-900 dark:hover:text-white transition-colors"
      >
        ← Voltar para a loja
      </Link>

      <div className="grid grid-cols-1 gap-10 lg:grid-cols-2">
        <ProductImageGallery images={product.images} productName={product.name} />

        <div className="space-y-6">
          <div className="space-y-2">
            <h1 className="text-3xl font-light tracking-tight text-primary-900 dark:text-white">
              {product.name}
            </h1>
            {product.category && storeSlug && (
              <Link
                to={`/stores/${storeSlug}#category-${product.category.slug}`}
                className="text-sm font-light text-primary-500 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 transition-colors"
              >
                {product.category.name}
              </Link>
            )}
          </div>

          <div className="flex flex-wrap items-baseline gap-3">
            <span className="text-2xl font-light text-primary-900 dark:text-white">
              {formatBrlCurrency(product.priceCents)}
            </span>
            {hasComparePrice && (
              <span className="text-lg font-light text-primary-400 line-through">
                {formatBrlCurrency(product.compareAtPriceCents!)}
              </span>
            )}
          </div>

          {product.description && (
            <p className="text-base font-light text-primary-700 dark:text-primary-300 whitespace-pre-wrap">
              {product.description}
            </p>
          )}

          <PaymentMethodSelector
            value={paymentMethodId}
            acceptedPaymentMethods={acceptedPaymentMethods}
            onChange={setPaymentMethodId}
          />

          <div className="flex flex-col sm:flex-row gap-3 pt-2">
            <WhatsAppButton
              phone={publicStore?.whatsappPhone}
              message={buildProductMessage({
                productName: product.name,
                priceCents: product.priceCents,
                productUrl: `${window.location.origin}${location.pathname}`,
                storeName: publicStore?.name,
                template: publicStore?.whatsappProductMessageTemplate,
                paymentMethodId,
              })}
              onClick={() => {
                if (!publicStore) return;
                trackWhatsAppClick(client, {
                  storeId: publicStore.id,
                  eventType: "whatsapp_product_clicked",
                  productId: product.id,
                  metadata: { productSlug: product.slug, paymentMethodId },
                });
              }}
            />
            <Button
              type="button"
              variant="outline"
              disabled={!isReady || (!canAddMore && !hasItem(product.id))}
              onClick={() =>
                addItem({
                  productId: product.id,
                  name: product.name,
                  slug: product.slug,
                  priceCents: product.priceCents,
                  thumbnailUrl: product.images[0]?.thumbnailUrl ?? null,
                })
              }
            >
              <ShoppingCart className="size-4" />
              {hasItem(product.id)
                ? `Adicionar mais (${getItemQuantity(product.id)})`
                : "Adicionar ao carrinho"}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
